import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { productAPI } from '../services/api';
import ProductCard from './products/ProductCard';

const UserDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [productStats, setProductStats] = useState({
    totalProducts: 0,
    categories: 0
  });

  useEffect(() => {
    loadFeaturedProducts();
    loadProductStats();
  }, []);

  const loadFeaturedProducts = async () => {
    try {
      setIsLoadingProducts(true);
      const response = await productAPI.getProducts({ 
        size: 4, 
        sortBy: 'createdAt', 
        sortDirection: 'desc' 
      });
      setFeaturedProducts(response.data.content || []);
    } catch (error) {
      console.error('Failed to load featured products:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const loadProductStats = async () => {
    try {
      const [productsResponse, categoriesResponse] = await Promise.all([
        productAPI.getProducts({ size: 1 }), // Just to get total count
        productAPI.getCategories()
      ]);
      
      setProductStats({
        totalProducts: productsResponse.data.totalElements || 0,
        categories: categoriesResponse.data.length || 0
      });
    } catch (error) {
      console.error('Failed to load product stats:', error);
    }
  };

  const handleProductClick = (product) => {
    navigate(`/products/${product.id}`);
  };

  const quickActions = [
    {
      title: 'Browse Products',
      description: 'Explore our complete catalog',
      path: '/products',
      icon: '🔍',
      color: '#007bff'
    },
    {
      title: 'My Orders',
      description: 'View your order history',
      path: '/orders',
      icon: '📋',
      color: '#28a745'
    },
    {
      title: 'Shopping Cart',
      description: 'Review items in your cart',
      path: '/cart',
      icon: '🛒',
      color: '#ffc107'
    }
  ];

  const handleActionClick = (path) => {
    if (path === '/orders') {
      // This feature will be implemented later
      alert('This feature will be available soon!');
      return;
    }
    navigate(path);
  };

  return (
    <div className="container">
      <div className="card">
        <h1>User Dashboard</h1>
        <div className="mb-4">
          <h3>Welcome, {user.firstName} {user.lastName}!</h3>
          <p><strong>Email:</strong> {user.email}</p>
          <p><strong>Role:</strong> User</p>
        </div>
        
        {/* Quick Actions */}
        <div className="card">
          <h3>Quick Actions</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '16px',
            marginBottom: '20px'
          }}>
            {quickActions.map((action, index) => (
              <div 
                key={index}
                className="card"
                onClick={() => handleActionClick(action.path)}
                style={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  borderLeft: `4px solid ${action.color}`,
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '';
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontSize: '24px' }}>{action.icon}</span>
                  <h4 style={{ margin: 0, color: action.color }}>{action.title}</h4>
                </div>
                <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                  {action.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Product Stats */}
        <div className="card">
          <h3>Store Overview</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div className="card" style={{ textAlign: 'center' }}>
              <h4>Available Products</h4>
              <p style={{ fontSize: '32px', color: '#007bff', margin: '8px 0' }}>
                {productStats.totalProducts}
              </p>
              <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                Products in our catalog
              </p>
            </div>
            
            <div className="card" style={{ textAlign: 'center' }}>
              <h4>Product Categories</h4>
              <p style={{ fontSize: '32px', color: '#28a745', margin: '8px 0' }}>
                {productStats.categories}
              </p>
              <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                Different categories available
              </p>
            </div>
          </div>
        </div>

        {/* Featured Products */}
        <div className="card">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: 0 }}>Recently Added Products</h3>
            <button
              onClick={() => navigate('/products')}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer',
                textDecoration: 'none'
              }}
            >
              View All Products →
            </button>
          </div>
          
          {isLoadingProducts ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Loading products...
            </div>
          ) : featuredProducts.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px'
            }}>
              {featuredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onClick={handleProductClick}
                />
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              color: '#6c757d'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
              <p>No products available yet.</p>
            </div>
          )}
        </div>

        {/* Coming Soon Features */}
        <div className="card">
          <h3>Coming Soon</h3>
          <p>Additional features that will be available:</p>
          <ul>
            <li>Order placement and tracking</li>
            <li>Wishlist management</li>
            <li>Product reviews and ratings</li>
            <li>User profile customization</li>
          </ul>
          <p><em>Stay tuned for these exciting updates!</em></p>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard; 